import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react'
import {
  AlertTriangle,
  CheckCircle2,
  History,
  Loader2,
  RefreshCw,
  UserPlus,
  Users,
} from 'lucide-react'
import * as api from '../api/auth'
import { ApiError } from '../api/auth'
import { AccessEditorDialog } from '../components/AccessEditorDialog'
import { EmailChangeDialog } from '../components/EmailChangeDialog'
import { DeleteAccountDialog } from '../components/DeleteAccountDialog'
import { InfoHint } from '../components/InfoHint'
import { Avatar } from '../components/Avatar'
import { grantableRoles, hasCapability, useAuth } from '../context/AuthContext'
import { entitledStores } from '../lib/access'
import {
  ROLE_LABELS,
  type ActivityEvent,
  type ManagedUser,
  type PermissionDefinition,
  type RoleKey,
} from '../types/identity'
import { storeScopeLabel } from '../lib/labels'

/** How many activity rows show before the log has to be expanded. */
const ACTIVITY_PREVIEW = 6

const INPUT =
  'w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-base sm:text-sm dark:border-white/10 dark:bg-white/5'
// Native dropdown popups are drawn by the OS, so they need their own colours — without
// this the options stayed white-on-white in dark mode (GM report, 2026-10-16).
const OPTION = 'bg-white text-slate-900 dark:bg-ink-900 dark:text-slate-100'
const SMALL_BUTTON =
  'inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-semibold text-salsa-600 transition-colors hover:bg-salsa-500/10 disabled:opacity-50 dark:text-salsa-400'

export function UsersPage() {
  const { user } = useAuth()
  const [users, setUsers] = useState<ManagedUser[]>([])
  const [events, setEvents] = useState<ActivityEvent[]>([])
  const [loading, setLoading] = useState(true)
  const [busyId, setBusyId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [emailTarget, setEmailTarget] = useState<ManagedUser | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<ManagedUser | null>(null)
  const [accessTarget, setAccessTarget] = useState<ManagedUser | null>(null)
  const [permissions, setPermissions] = useState<PermissionDefinition[]>([])
  const canDelete = hasCapability(user, 'accounts.delete')
  const canInvite = hasCapability(user, 'staff.invite')
  const canActivate = hasCapability(user, 'accounts.activate')
  const canEditAccess = Boolean(user?.can_edit_access)
  const canViewActivity = Boolean(user?.can_view_audit)
  const [search, setSearch] = useState('')
  // The activity log is context, not the page's job — it stays compact (GM, 2026-10-17).
  const [showAllActivity, setShowAllActivity] = useState(false)

  const [invite, setInvite] = useState({
    email: '',
    role: 'team_member' as RoleKey,
    // District managers have no single `store_id`; the first shop in their
    // effective scope is selected once the filtered catalogue arrives.
    store_id: (user?.store_id ?? null) as number | null,
    scope_store_ids: [] as number[],
  })
  const [stores, setStores] = useState<api.StoreOption[]>([])

  const roles = useMemo(() => grantableRoles(user), [user])
  const visibleActivity = showAllActivity ? events : events.slice(0, ACTIVITY_PREVIEW)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const activityPromise = canViewActivity
        ? api.fetchActivity({ limit: 20 })
        : Promise.resolve([] as ActivityEvent[])
      const [people, activity, storeList, catalogue] = await Promise.all([
        api.listUsers({ search: search || undefined }),
        activityPromise,
        api.fetchStores(),
        api.fetchPermissions(),
      ])
      setUsers(people)
      setEvents(activity)
      // Invitations and access edits may only target shops the actor actually
      // holds. `/auth/stores` is the master catalogue for every shop.
      setStores(entitledStores(user, storeList))
      setPermissions(catalogue)
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not load accounts')
    } finally {
      setLoading(false)
    }
  }, [search, canViewActivity, user])

  useEffect(() => {
    void load()
  }, [load])

  useEffect(() => {
    if (invite.store_id !== null || stores.length === 0) return
    setInvite((current) => ({ ...current, store_id: stores[0].id }))
  }, [invite.store_id, stores])

  async function run(id: string | null, action: () => Promise<string | null>) {
    setBusyId(id)
    setError(null)
    setNotice(null)
    try {
      const message = await action()
      if (message) setNotice(message)
      await load()
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Action failed')
    } finally {
      setBusyId(null)
    }
  }

  const handleInvite = (event: FormEvent) => {
    event.preventDefault()
    void run(null, async () => {
      const result = await api.inviteUser({
        email: invite.email,
        role: invite.role,
        // The store the role needs (D-42): nothing for company-wide, a selection for a
        // district manager, one shop for everybody else.
        store_id: invite.role === 'operations_manager' ? null : invite.store_id,
        scope_store_ids: invite.role === 'district_manager' ? invite.scope_store_ids : undefined,
      })
      setInvite({ ...invite, email: '' })
      return `Invitation sent to ${result.email} as ${ROLE_LABELS[result.role]}.`
    })
  }

  const isSelf = (row: ManagedUser) => row.id === user?.id

  const rowActions = (row: ManagedUser): Array<{ label: string; run: () => void; disabled?: boolean }> => [
    { label: 'E-mail', run: () => setEmailTarget(row) },
    {
      label: 'Reset link',
      run: () =>
        void run(row.id, async () => {
          await api.sendUserPasswordReset(row.id)
          return `Reset link sent to ${row.email}.`
        }),
    },
    {
      label: 'Reset 2FA',
      run: () =>
        void run(row.id, async () => {
          await api.resetUserTwoFactor(row.id)
          return `2FA reset for ${row.full_name}.`
        }),
    },
    ...(canEditAccess && row.role !== 'system_admin'
      ? [
          {
            label: 'Access',
            run: () => setAccessTarget(row),
          },
        ]
      : []),
    ...(canActivate
      ? [
          {
            label: row.is_active ? 'Disable' : 'Enable',
            disabled: isSelf(row),
            run: () =>
              void run(row.id, async () => {
                await api.setUserActivation(row.id, !row.is_active)
                return `${row.full_name} ${row.is_active ? 'deactivated' : 'activated'}.`
              }),
          },
        ]
      : []),
    ...(canDelete
      ? [
          {
            label: 'Delete',
            run: () => setDeleteTarget(row),
          },
        ]
      : []),
  ]

  return (
    <div className="space-y-5">
      {error && (
        <div className="flex items-start gap-2 rounded-2xl border border-salsa-200 bg-salsa-50/70 px-4 py-3 text-xs text-salsa-800 dark:border-salsa-500/25 dark:bg-salsa-500/10 dark:text-salsa-200">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          <span>{error}</span>
        </div>
      )}

      {notice && (
        <div className="flex items-start gap-2 rounded-2xl border border-emerald-200 bg-emerald-50/70 px-4 py-3 text-xs text-emerald-800 dark:border-emerald-500/25 dark:bg-emerald-500/10 dark:text-emerald-200">
          <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          <span>{notice}</span>
        </div>
      )}

      {canInvite && (
        <section className="card space-y-4 p-4">
          <div className="flex flex-wrap items-center gap-2">
            <UserPlus className="h-4 w-4 text-salsa-500" aria-hidden="true" />
            <h2 className="text-sm font-semibold text-slate-900 dark:text-white">Invite a colleague</h2>
          <InfoHint
            label="Who you may invite, and where"
            title="Roles Strictly Below Your Own, Within Your Scope"
          >
            You may only invite roles that sit strictly below your own, and only into the stores
            you hold yourself. System Admin is exempt in both directions: it may invite any role
            below it, in any store — and it can never be handed out from here, by anybody.
          </InfoHint>
        </div>

        <form onSubmit={handleInvite} className="grid gap-3 lg:grid-cols-4">
          <input
            type="email"
            value={invite.email}
            onChange={(event) => setInvite({ ...invite, email: event.target.value })}
            placeholder="name@email.com"
            className={INPUT}
            required
          />
          <select
            value={invite.role}
            onChange={(event) => setInvite({ ...invite, role: event.target.value as RoleKey })}
            className={`${INPUT} dark:[color-scheme:dark]`}
          >
            {roles.map((role) => (
              <option key={role} value={role} className={OPTION}>
                {ROLE_LABELS[role as RoleKey] ?? role}
              </option>
            ))}
          </select>
          {invite.role === 'operations_manager' ? (
            <div className="flex items-center rounded-xl border border-emerald-200 bg-emerald-50/70 px-3 py-2 text-xs text-emerald-800 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-200">
              All {stores.length} shops — company-wide access, nothing to pick
            </div>
          ) : invite.role === 'district_manager' ? (
            <div className="rounded-xl border border-slate-200 bg-white p-2 lg:col-span-4 dark:border-white/10 dark:bg-white/5">
              <p className="px-1 pb-1 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                Shops in this district ({invite.scope_store_ids.length} selected)
              </p>
              <div className="max-h-40 space-y-0.5 overflow-y-auto">
                {stores.map((store) => (
                  <label
                    key={store.id}
                    className="flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1 text-xs hover:bg-slate-50 dark:hover:bg-white/5"
                  >
                    <input
                      type="checkbox"
                      checked={invite.scope_store_ids.includes(store.id)}
                      onChange={(event) =>
                        setInvite({
                          ...invite,
                          scope_store_ids: event.target.checked
                            ? [...invite.scope_store_ids, store.id].sort((a, b) => a - b)
                            : invite.scope_store_ids.filter((id) => id !== store.id),
                        })
                      }
                      className="h-4 w-4 rounded border-slate-300 accent-salsa-500"
                    />
                    <span className="text-slate-700 dark:text-slate-200">{store.label}</span>
                  </label>
                ))}
              </div>
              <div className="flex gap-3 px-2 pt-1">
                <button
                  type="button"
                  onClick={() =>
                    setInvite({ ...invite, scope_store_ids: stores.map((store) => store.id) })
                  }
                  className="text-[11px] font-semibold text-salsa-600 hover:underline dark:text-salsa-400"
                >
                  Select all
                </button>
                <button
                  type="button"
                  onClick={() => setInvite({ ...invite, scope_store_ids: [] })}
                  className="text-[11px] font-semibold text-slate-400 hover:underline"
                >
                  Clear
                </button>
              </div>
            </div>
          ) : (
            <select
              value={invite.store_id ?? ''}
              onChange={(event) => setInvite({ ...invite, store_id: Number(event.target.value) })}
              className={`${INPUT} dark:[color-scheme:dark]`}
              disabled={Boolean(user?.store_id) && !user?.is_platform_admin}
              aria-label="Store"
            >
              {stores.map((store) => (
                <option key={store.id} value={store.id} className={OPTION}>
                  {store.label}
                </option>
              ))}
            </select>
          )}
          <button
            type="submit"
            disabled={busyId !== null}
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-salsa-500 px-4 py-2 text-sm font-semibold text-white hover:bg-salsa-600 disabled:opacity-60"
          >
            {busyId === null && <UserPlus className="h-4 w-4" aria-hidden="true" />}
            Send invitation
          </button>
        </form>

        </section>
      )}

      <section className="card space-y-3 p-4">
        <div className="flex flex-wrap items-center gap-2">
          <Users className="h-4 w-4 text-salsa-500" aria-hidden="true" />
          <h2 className="text-sm font-semibold text-slate-900 dark:text-white">
            Accounts you manage ({users.length})
          </h2>
          <div className="ml-auto flex items-center gap-2">
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search name or e-mail"
              className={`${INPUT} w-56`}
            />
            <button type="button" onClick={() => void load()} className="icon-button" title="Refresh">
              <RefreshCw className={loading ? 'h-4 w-4 animate-spin' : 'h-4 w-4'} aria-hidden="true" />
            </button>
          </div>
        </div>

        {loading && users.length === 0 ? (
          <p className="flex items-center gap-2 py-6 text-xs text-slate-500 dark:text-slate-400">
            <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" /> Loading accounts…
          </p>
        ) : users.length === 0 ? (
          <p className="py-6 text-xs text-slate-500 dark:text-slate-400">
            No accounts in your scope yet — send an invitation above.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs md:min-w-[52rem]">
              <thead className="text-[10px] uppercase tracking-wider text-slate-400 dark:text-slate-500">
                <tr>
                  <th className="py-2">Person</th>
                  <th className="py-2">Role</th>
                  <th className="hidden py-2 md:table-cell">Store</th>
                  <th className="hidden py-2 md:table-cell">2FA</th>
                  <th className="py-2">Status</th>
                  <th className="hidden py-2 md:table-cell">Last sign-in</th>
                  <th className="py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-white/5">
                {users.map((row) => (
                  <tr key={row.id} className={row.is_active ? undefined : 'opacity-60'}>
                    <td className="py-2 pr-3">
                      <div className="flex items-center gap-2">
                        <Avatar name={row.full_name} avatar={row.avatar} size="sm" />
                        <div className="min-w-0">
                          <span className="block font-medium text-slate-800 dark:text-slate-100">
                            {row.full_name}
                            {isSelf(row) && <span className="pill ml-2 py-0.5 text-[10px]">you</span>}
                            {row.nickname && (
                              <span className="ml-2 text-[11px] font-normal text-slate-400 dark:text-slate-500">
                                “{row.nickname}”
                              </span>
                            )}
                          </span>
                          <span className="text-slate-500 dark:text-slate-400">{row.email}</span>
                        </div>
                      </div>
                    </td>
                    <td className="py-2 pr-3">{ROLE_LABELS[row.role] ?? row.role}</td>
                    <td className="hidden py-2 pr-3 font-mono text-[11px] md:table-cell">
                      {storeScopeLabel(row)}
                    </td>
                    <td className="hidden py-2 pr-3 md:table-cell">
                      <span
                        className={`badge ${
                          row.two_factor_enabled
                            ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300'
                            : 'bg-slate-500/15 text-slate-600 dark:text-slate-300'
                        }`}
                      >
                        {row.two_factor_enabled ? 'on' : 'off'}
                      </span>
                    </td>
                    <td className="py-2 pr-3">
                      <span
                        className={
                          row.is_active
                            ? 'text-emerald-600 dark:text-emerald-400'
                            : 'text-salsa-600 dark:text-salsa-400'
                        }
                      >
                        {row.is_active ? 'active' : 'disabled'}
                      </span>
                      {row.is_locked && <span className="ml-1 text-amber-600">locked</span>}
                    </td>
                    <td className="hidden py-2 pr-3 text-slate-500 md:table-cell dark:text-slate-400">
                      {row.last_login_at ? new Date(row.last_login_at).toLocaleString() : 'never'}
                    </td>
                    <td className="py-2 text-right">
                      {/* Your own row is deliberately empty — no actions on yourself at all
                          (GM, 2026-10-17): every change to your own account goes through
                          Account Settings, and an operator is not something to advertise. */}
                      {isSelf(row) ? null : (
                        <div className="flex flex-wrap justify-end gap-1">
                          {rowActions(row).map((action) => (
                            <button
                              key={action.label}
                              type="button"
                              className={SMALL_BUTTON}
                              disabled={busyId === row.id || Boolean(action.disabled)}
                              onClick={action.run}
                            >
                              {action.label}
                            </button>
                          ))}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {canViewActivity && (
        <section className="card p-3">
          <div className="flex flex-wrap items-center gap-2">
            <History className="h-3.5 w-3.5 text-slate-400" aria-hidden="true" />
          <h2 className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
            Activity in your scope
          </h2>
          <span className="badge bg-slate-500/10 text-slate-500 dark:text-slate-300">
            {events.length}
          </span>
          {events.length > ACTIVITY_PREVIEW && (
            <button
              type="button"
              onClick={() => setShowAllActivity((value) => !value)}
              className="ml-auto text-[11px] font-semibold text-salsa-600 hover:underline dark:text-salsa-400"
            >
              {showAllActivity ? 'Show less' : `Show all ${events.length}`}
            </button>
          )}
        </div>
        <ul className="mt-2 grid gap-x-4 gap-y-1 md:grid-cols-2">
          {visibleActivity.map((event) => (
            <li key={event.id} className="flex items-center gap-1.5 text-[11px]">
              <span
                className={`badge ${
                  event.outcome === 'success'
                    ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300'
                    : event.outcome === 'warning'
                      ? 'bg-amber-500/15 text-amber-700 dark:text-amber-300'
                      : 'bg-salsa-500/15 text-salsa-700 dark:text-salsa-300'
                }`}
              >
                {event.event.replace(/_/g, ' ')}
              </span>
              <span className="truncate text-slate-500 dark:text-slate-400">
                {event.email ?? 'unknown'}
              </span>
              <span className="ml-auto shrink-0 font-mono text-[10px] text-slate-400 dark:text-slate-500">
                {new Date(event.created_at).toLocaleString()}
              </span>
            </li>
          ))}
          {events.length === 0 && (
            <li className="text-[11px] text-slate-500 dark:text-slate-400">
              No activity recorded yet.
            </li>
          )}
          </ul>
        </section>
      )}

      {accessTarget && user && (
        <AccessEditorDialog
          target={accessTarget}
          actor={user}
          roles={roles}
          stores={stores}
          catalog={permissions}
          onClose={() => setAccessTarget(null)}
          onSaved={(message) => {
            setNotice(message)
            void load()
          }}
        />
      )}

      {emailTarget && (
        <EmailChangeDialog
          target={emailTarget}
          onClose={() => setEmailTarget(null)}
          onSaved={(message) => {
            setNotice(message)
            void load()
          }}
        />
      )}

      {deleteTarget && (
        <DeleteAccountDialog
          target={deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onDeleted={(message) => {
            setNotice(message)
            void load()
          }}
        />
      )}
    </div>
  )
}
